import { getPaymentMethods, storePaymentMethods } from "./getPaymentMethods";
import { getCustomerProfile } from "./getCustomerProfile";
import { sendBugsnagError } from "@utils/Bugsnag";
import { getFormValues } from "redux-form";
import { trimString, removeEmpty } from "@utils";
import { errorResponseMessage } from "./errorResponseMessage";
import { isSchemaError } from "./isSchemaError";
import { defaultAction } from "./defaultAction";
import {
  SET_DEFAULT_SELL_ACH_ACCOUNT,
  SET_SELECTED_PAYMENT_ID,
  SET_ADDED_PAYMENT_METHOD_ID,
  REMOVE_CREDIT_CARD_MODAL_OPEN,
  OPEN_ERROR_BOX,
  HIDE_LOADING,
  CC_API_ERR_RESPONSE
} from "@constants/actionTypes";
import {
  getMainCheckoutAddress,
  getCustomerAddress
} from "./getMainCheckoutAddress";

export const deletePaymentMethod =
  paymentMethod => async (dispatch, getState, api) => {
    try {
      let newMethods = [];
      switch (paymentMethod.paymentMethod) {
        case "CREDITCARD":
          await api.delete(
            `v3/customers/creditcards/${paymentMethod.creditCardId}`
          );
          newMethods = await dispatch(getPaymentMethods());
          break;
        case "ACH":
          await api.delete(
            `v3/customers/bankaccounts/${paymentMethod.bankAccountId}`
          );
          newMethods = await dispatch(getPaymentMethods());

          break;
        case "ACH_CREDITCARD":
          await api.delete(
            `v3/customers/bankaccounts/${paymentMethod.bankAccountId}`
          );
          newMethods = await dispatch(getPaymentMethods());
          break;
        default:
          break;
      }
      return newMethods;
    } catch (error) {
      sendBugsnagError(error, "actions/deletePaymentMethod");
    }
  };

export const postNewPaymentMethod =
  (obj, formName) => async (dispatch, getState, api) => {
    try {
      if (formName === "addACreditCard") {
        const creditCardFormData = getFormValues("addACreditCard")(getState());
        const { customerInfo } = getState();
        let monthAndYear =
          creditCardFormData.creditCardInformation.ExpirationDate.replace(
            /\s+/g,
            ""
          ).split("/");

        obj.creditCardData.creditCard.expMonth = trimString(monthAndYear[0]);
        obj.creditCardData.creditCard.expYear = trimString(monthAndYear[1]);
        obj.creditCardData.creditCard.save = true;

        obj.default =
          creditCardFormData?.creditCardInformation?.saveAsDefault || false;
        const billing = creditCardFormData?.creditCardInformation
          ?.creditBillingCheckbox
          ? {
              firstname: customerInfo.firstname,
              lastname: customerInfo.lastname,
              street: customerInfo.street,
              street2: customerInfo.street2,
              city: customerInfo.city,
              state: customerInfo.state,
              postcode: customerInfo.zip
            }
          : {
              firstname: creditCardFormData.creditBilling.firstname,
              lastname: creditCardFormData.creditBilling.lastname,
              street: creditCardFormData.creditBilling.street,
              street2: creditCardFormData.creditBilling.street2,
              city: creditCardFormData.creditBilling.city,
              state: creditCardFormData.creditBilling.state,
              postcode: creditCardFormData.creditBilling.zip
            };
        obj.creditCardData.billing = removeEmpty(billing);
      }

      const results = await api.post("v3/customers/payment-options", obj);
      if (results?.data?.data) {
        await dispatch(storePaymentMethods(results));
      }
      if (
        obj.type === "sell" &&
        obj.paymentMethod === "ACH" &&
        results?.data?.data
      ) {
        dispatch(defaultAction(SET_DEFAULT_SELL_ACH_ACCOUNT, obj.bankAccount));
      }
      return results;
    } catch (error) {
      sendBugsnagError(error, "actions/postNewPaymentMethod");
      let errorMessage = errorResponseMessage(error);
      if (!errorMessage) {
        errorMessage =
          "Something went wrong, please check your information and try again";
      }
      return { error: errorMessage };
    }
  };

export const createPaymentMethod =
  ({
    paymentMethod,
    defaultSelected = false,
    type,
    cardNumToken,
    cvvNumToken
  }) =>
  async (dispatch, getState, api) => {
    try {
      const payObj = {
        paymentMethod,
        default: defaultSelected,
        type
      };
      const mainCheckoutBuy = getFormValues("mainCheckoutBuy")(getState());
      if (paymentMethod.includes("ACH")) {
        if (
          mainCheckoutBuy &&
          mainCheckoutBuy.enterACH &&
          mainCheckoutBuy.enterACH.achRow &&
          mainCheckoutBuy.enterACH.achRow.paymentDetails
        ) {
          payObj.bankAccount = mainCheckoutBuy.enterACH.achRow.paymentDetails;
        } else {
          payObj.bankAccount = {
            routingNumber: mainCheckoutBuy.enterACH.routingNumber,
            accountNumber: mainCheckoutBuy.enterACH.accountNumber,
            accountType: mainCheckoutBuy.enterACH.accountType.toLowerCase(),
            save: !!mainCheckoutBuy.enterACH.saveBankAccount
          };
        }

        payObj.default = !!mainCheckoutBuy.saveAsDefaultAch;
      }

      if (cardNumToken || (mainCheckoutBuy && mainCheckoutBuy.backupCCForACH)) {
        let billingAddress = {};
        if (mainCheckoutBuy) {
          const formShippingAddress = await dispatch(
            getMainCheckoutAddress("shippingAddress")
          );
          const formBillingAddress = await dispatch(
            getMainCheckoutAddress("billingAddress")
          );

          if (mainCheckoutBuy.billingAddress.sameAsShipping) {
            billingAddress = formShippingAddress;
          } else {
            billingAddress = formBillingAddress;
          }
        } else {
          billingAddress = await dispatch(getCustomerAddress());
        }

        if (paymentMethod === "CREDITCARD") {
          payObj.default = mainCheckoutBuy.creditCardInformation.saveAsDefault;
        }

        if (cardNumToken) {
          let monthAndYear;
          if (paymentMethod === "ACH_CREDITCARD") {
            monthAndYear =
              mainCheckoutBuy.paymentInfoForACH.creditCardInformation.ExpirationDate.replace(
                /\s+/g,
                ""
              ).split("/");
          } else {
            monthAndYear =
              mainCheckoutBuy.creditCardInformation.ExpirationDate.replace(
                /\s+/g,
                ""
              ).split("/");
          }
          payObj.creditCardData = {
            creditCard: {
              numberToken: cardNumToken,
              cvvToken: cvvNumToken,
              expMonth: trimString(monthAndYear[0]),
              expYear: trimString(monthAndYear[1]),
              save: true
            },
            billing: removeEmpty(billingAddress)
          };
        } else {
          payObj.creditCardData = {
            id: mainCheckoutBuy.paymentInfoForACH.ccRow.creditCardId
          };
        }
      }
      const results = await dispatch(postNewPaymentMethod(payObj));
      if (results?.data?.data) {
        dispatch(
          defaultAction(SET_SELECTED_PAYMENT_ID, results.data.data.createdId)
        );
        dispatch(
          defaultAction(
            SET_ADDED_PAYMENT_METHOD_ID,
            results.data.data.createdId
          )
        );
        return "success";
      } else {
        return results;
      }
    } catch (error) {
      sendBugsnagError(error, "actions/createPaymentMethod");
      return error;
    }
  };

export const deleteACH = bankAccount => async (dispatch, getState, api) => {
  try {
    await api.delete(`v3/customers/bankaccounts/${bankAccount.bankAccountId}`);
    await dispatch(getPaymentMethods());
    await dispatch(getCustomerProfile());
    return "success";
  } catch (error) {
    sendBugsnagError(error, "actions/deleteACH");
    let message = errorResponseMessage(error);
    if (isSchemaError(message)) {
      message = "Something went wrong, please refresh the page.";
    }
    return { error: message };
  }
};

export const addACH = payload => async (dispatch, getState, api) => {
  try {
    const payObj = {
      paymentMethod: "ACH",
      default: payload.default,
      type: "buy",
      bankAccount: {
        routingNumber: payload.routingNumber,
        accountNumber: payload.accountNumber,
        accountType: payload.accountType,
        save: true
      }
    };

    const results = await dispatch(postNewPaymentMethod(payObj));
    if (results?.data?.data) {
      await dispatch(getPaymentMethods());
      await dispatch(getCustomerProfile());
      return "success";
    } else {
      throw new Error(results);
    }
  } catch (error) {
    sendBugsnagError(error, "actions/addACH");
    let message = errorResponseMessage(error);
    if (isSchemaError(message)) {
      message = "Something went wrong, please refresh the page.";
    }
    return { error: message };
  }
};

export const deleteCreditCard = ccMethod => async (dispatch, getState, api) => {
  try {
    await api.delete(`v3/customers/creditcards/${ccMethod.creditCardId}`);
    await dispatch(getPaymentMethods());
    await dispatch(getCustomerProfile());
    return "success";
  } catch (error) {
    sendBugsnagError(error, "actions/deleteCreditCard");
    dispatch(defaultAction(REMOVE_CREDIT_CARD_MODAL_OPEN, false));
    dispatch(
      defaultAction(OPEN_ERROR_BOX, {
        title: "Unable to delete credit card",
        message:
          "We are sorry. Please refresh the page and try again. Thank you."
      })
    );
    dispatch(defaultAction(HIDE_LOADING));
    dispatch(
      defaultAction(CC_API_ERR_RESPONSE, {
        message: "Server Error: Unable to Delete Credit Card At This Time"
      })
    );
  }
};

export const updatePaymentMethod = id => async (dispatch, getState, api) => {
  const mainCheckoutBuy = getFormValues("mainCheckoutBuy")(getState());
  if (mainCheckoutBuy && mainCheckoutBuy.saveAsDefaultOldMethod) {
    const updateObject = {
      ID: id,
      default: true,
      scope: "buy"
    };

    try {
      const results = await api.put(
        `v3/customers/payment-options/${id}`,
        updateObject
      );
      dispatch(storePaymentMethods(results));
    } catch (error) {
      sendBugsnagError(error, "actions/updatePaymentMethod");
    }
  }
};
